import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Section, Container, SectionTitle } from '../styles/StyledComponents';

const TestimonialsContainer = styled(Container)`
  text-align: center;
`;

const TestimonialSlider = styled.div`
  position: relative;
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 0;
`;

const TestimonialCard = styled(motion.div)`
  background-color: var(--white);
  border-radius: 8px;
  padding: 2.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  margin: 0 auto;
  max-width: 700px;
  position: relative;
  
  &:before {
    content: '"';
    position: absolute;
    top: 20px;
    left: 20px;
    font-size: 5rem;
    font-family: 'Georgia', serif;
    color: var(--light-blue);
    line-height: 1;
    opacity: 0.5;
  }
`;

const TestimonialQuote = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  color: var(--dark-blue);
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AuthorImage = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorInfo = styled.div`
  text-align: left;
`;

const AuthorName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
  color: var(--dark-blue);
`;

const AuthorTitle = styled.p`
  font-size: 0.9rem;
  color: var(--medium-gray);
`;

const SliderControls = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const SliderDot = styled.button<{ active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.active ? 'var(--primary-blue)' : 'var(--light-blue)'};
  border: none;
  margin: 0 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-blue)' : 'var(--secondary-blue)'};
  }
`;

const Testimonials: React.FC = () => {
  const [currentTestimonial, setCurrentTestimonial] = React.useState(0);
  
  const testimonials = [
    {
      quote: "Karen Mae is incredibly detail-oriented and ensures accurate documentation.",
      name: "Regine Hoybia",
      title: "Former Colleague",
      image: "https://placehold.co/200x200/EBF5FF/1A365D?text=RH"
    },
    {
      quote: "Her ability to multitask and manage pharmacy operations is unmatched.",
      name: "Juvelyn Pacaldo",
      title: "Former Senior",
      image: "https://placehold.co/200x200/EBF5FF/1A365D?text=JP"
    },
    {
      quote: "Boost your product and service's credibility by adding testimonials from your clients. People love recommendations so feedback from others who've tried it is invaluable.",
      name: "Ariel Angus",
      title: "Former Ultramart COO",
      image: "https://placehold.co/200x200/EBF5FF/1A365D?text=AA"
    }
  ];
  
  const handleDotClick = (index: number) => {
    setCurrentTestimonial(index);
  };
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [testimonials.length]);
  
  return (
    <Section id="testimonials" style={{ backgroundColor: 'var(--light-gray)' }}>
      <TestimonialsContainer>
        <SectionTitle
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Testimonials
        </SectionTitle>
        
        <TestimonialSlider>
          <TestimonialCard
            key={currentTestimonial}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5 }}
          >
            <TestimonialQuote>
              {testimonials[currentTestimonial].quote}
            </TestimonialQuote>
            <TestimonialAuthor>
              <AuthorImage>
                <img src={testimonials[currentTestimonial].image} alt={testimonials[currentTestimonial].name} />
              </AuthorImage>
              <AuthorInfo>
                <AuthorName>{testimonials[currentTestimonial].name}</AuthorName>
                <AuthorTitle>{testimonials[currentTestimonial].title}</AuthorTitle>
              </AuthorInfo>
            </TestimonialAuthor>
          </TestimonialCard>
          
          <SliderControls>
            {testimonials.map((_, index) => (
              <SliderDot 
                key={index} 
                active={currentTestimonial === index} 
                onClick={() => handleDotClick(index)}
                aria-label={`View testimonial ${index + 1}`}
              />
            ))}
          </SliderControls>
        </TestimonialSlider>
      </TestimonialsContainer>
    </Section>
  );
};

export default Testimonials;
